import { createFileRoute, notFound } from "@tanstack/react-router"
import { ArrowLeftRight, BookOpen, FileSpreadsheet, Route as RouteIcon } from "lucide-react"

import { breadcrumbJsonLd, buildHead, webPageJsonLd } from "@/core/seo"

import {
  CompareHeader,
  Cites,
  HowWeCheck,
  Legend,
  Section,
  SourceList,
  linkClass,
} from "../components/compare/compare-parts"
import { formatCheckedOn } from "../components/home/home-content"
import { AnswerBox, Breadcrumb, CtaBand } from "../components/landing"
import { PickPair } from "../components/landing/page-parts"
import { IconTile } from "../components/landing/product-art"
import { hub } from "../content/compare"
import { COMPARE_PUBLISHED, compareReviewed } from "../content/compare/publish"

/** Copy source: docs/design/landing/copy-compare.md section 1, trimmed by T407 D3 and D6. */
export const compareSeo = () =>
  buildHead({
    title: "Compare mileage and receipt apps",
    description:
      "Fair, dated comparisons of Kestly with Driversnote and other apps for Australian sole traders. Every fact comes from the other app's own page.",
    path: "/compare",
    jsonLd: [
      breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "Compare" }]),
      webPageJsonLd({
        name: "Alternatives for Australian sole traders",
        path: "/compare",
        dateModified: compareReviewed["/compare"],
      }),
    ],
  })

export const Route = createFileRoute("/_brand-layout/compare")({
  beforeLoad: () => {
    if (!COMPARE_PUBLISHED) throw notFound()
  },
  head: () => {
    const { meta, links, scripts } = compareSeo()
    return { meta, links, scripts }
  },
  component: ComparePage,
})

const CARDS = [
  {
    icon: ArrowLeftRight,
    tone: "primary" as const,
    href: "/compare/driversnote-alternative",
    title: "Driversnote alternative",
    body: "Kestly and Driversnote side by side, including where Driversnote is stronger.",
  },
  {
    icon: FileSpreadsheet,
    tone: "success" as const,
    href: "/compare/spreadsheet-logbook",
    title: "Spreadsheet logbook",
    body: "Keeping the 12-week logbook by hand: what it costs in time and what you need to record.",
  },
]

export function ComparePage() {
  const reviewed = compareReviewed["/compare"]
  return (
    <>
      <Breadcrumb items={[{ label: "Home", href: "/" }, { label: "Compare" }]} />
      <CompareHeader reviewed={reviewed} title="Alternatives for Australian sole traders">
        <AnswerBox>
          Kestly records trips, reads receipts and sends tax invoices. These pages compare it with
          apps people often look at first, using each company&apos;s own pages.
        </AnswerBox>
      </CompareHeader>
      <Section title="Pick a comparison">
        <div className="grid gap-4 md:grid-cols-2">
          {CARDS.map((c) => (
            <div
              key={c.href}
              className="border-border bg-card grid content-start gap-2 rounded-xl border p-5"
            >
              <IconTile icon={c.icon} tone={c.tone} />
              <b className="text-lg">{c.title}</b>
              <span className="text-muted-foreground">{c.body}</span>
              <a
                href={c.href}
                className={`${linkClass} inline-flex min-h-11 items-center font-semibold`}
              >
                Read the comparison
              </a>
            </div>
          ))}
        </div>
      </Section>
      <Section title="Free plans at a glance">
        <Legend checked={formatCheckedOn(reviewed)} />
        <div
          role="region"
          aria-label="Free plans at a glance"
          tabIndex={0}
          className="border-border bg-card focus-visible:ring-ring overflow-x-auto rounded-lg border outline-none focus-visible:ring-2"
        >
          <table className="w-full border-collapse text-left text-sm">
            <caption className="sr-only">Free plans at a glance</caption>
            <thead>
              <tr className="bg-muted">
                <th scope="col" className="px-4 py-3 text-[13px] font-semibold">
                  App
                </th>
                <th scope="col" className="px-4 py-3 text-[13px] font-semibold">
                  Free plan or trial
                </th>
              </tr>
            </thead>
            <tbody>
              {hub.rows.map((row) => (
                <tr key={row.label} className="border-border border-t">
                  <th scope="row" className="px-4 py-3 align-top font-medium">
                    {row.label}
                  </th>
                  <td className="px-4 py-3 align-top">
                    {row.theirs.value} <Cites ids={row.theirs.sources} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-muted-foreground text-sm">
          From each company&apos;s own pages, opened {formatCheckedOn(reviewed)}. Free-plan terms
          differ by what they count, so this is not a ranking. For Everlance the source is its
          Australian App Store page, which changes often, so we re-check it more often.
        </p>
      </Section>
      <Section title="Which kind of app fits">
        <PickPair
          items={[
            {
              icon: RouteIcon,
              tone: "warning",
              title: "Pick a mileage-only app if",
              body: "You are an employee, you only claim car costs, and you do not need invoices or receipts in the same place.",
            },
            {
              icon: BookOpen,
              tone: "primary",
              title: "Pick Kestly if",
              body: "You have an ABN and want trips, receipts and invoices in one place, ready for your accountant.",
            },
          ]}
        />
        <p>
          See the{" "}
          <a href="/mileage-tracker" className={linkClass}>
            mileage tracker
          </a>
          ,{" "}
          <a href="/logbook-method" className={linkClass}>
            logbook method
          </a>{" "}
          and{" "}
          <a href="/pricing" className={linkClass}>
            pricing
          </a>
          .
        </p>
      </Section>
      <HowWeCheck />
      <Section title="Sources">
        <SourceList page={hub} />
      </Section>
      <CtaBand
        title="See it with your own trips"
        description="Create a free account and record your first trips."
        ctaLabel="Create a free account"
        ctaHref="/login"
      />
    </>
  )
}
